import React, { useEffect, useRef, useState } from 'react';
import { CSVLink } from 'react-csv';
import SearchIcon from '@mui/icons-material/Search';
import { useSelector, useDispatch } from 'react-redux';
import { Grid,
    Box,
    Typography,
    Pagination,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Skeleton,
    Paper, IconButton, InputBase,
    TableSortLabel,
    Avatar } from '@mui/material';
import { CustomButton, CustomModal } from '../../../shared';
import { ArrowLeftIcon,
    ArrowRightIcon,
    CheckCircleIcon,
    CrossCircleIcon,
    DownloadIcon } from '../../../../assets';
import { actions, actions as triggerActions } from '../../../../redux/reducers/triggerReducer';
import { calculateMaxWidth, formatDateForExport, getSortedData, handleHideSpecialtyColumns, handleRowClassForEngagementsTable } from '../../../../utility';
import { REGENERON_COMPANY_ID } from '../../../../constants';
import { actions as prescriptionActions } from '../../../../redux/reducers/prescriptionReducer';
import PrescribersProfile from '../../dashboard/topPrescribers/prescriberProfile';
import { handleIcons, showExtraColumns } from '../../../../utility/companySpecificFunctions';
import { getTriggersSummaryPaginate } from '../../../../redux/thunks/triggerThunk';

const TRIGGER_TABLE_HEADERS = [
    { key: 'trigger_name', label: 'Trigger Name' },
    { key: 'triggerDate', label: 'Trigger Date' },
    { key: 'npi', label: 'NPI' },
    { key: 'name', label: 'Name' },
    { key: 'location', label: 'Location' },
    { key: 'segment', label: 'Physician Segment' },
    { key: 'specialty', label: 'Physician Specialty' },
    { key: 'digital_engagement_priority', label: 'Digital Engagement' },
    { key: 'suggest_fmt_trigger', label: 'FMT Match' },
    { key: 'fieldEngagement', label: 'Field Engagement' }

];

function TriggerTable(props) {
    const dispatch = useDispatch();

    const {
        triggerSummary: {
            currentPage,
            pageSize,
            selectedTriggers,
            totalPages,
            isTriggerSummaryPaginated,
            totalPhysicianCount,
            allPhysicians, filteredPhysicians, name: triggerName 
        }
    } = useSelector((state) => state.trigger);
    const { company } = useSelector((state) => state.company);
    const { startDateRange, endDateRange } = useSelector((state) => state.filter);
    const { setTriggerSummaryCurrentPage, setDownloading } = triggerActions;
    const [order, setOrder] = useState('asc');
    const [orderBy, setOrderBy] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [isDownloading, setIsDownloading] = useState(true);
    const [csvData, setCSVData] = useState({
        name: '',
        headers: [],
        data: []
    });
    const [isLoading, setIsLoading] = useState(false);
    const [maxWidth, setMaxWidth] = useState(calculateMaxWidth());
    const { setSelectedNPI } = prescriptionActions;
    const showFMT = showExtraColumns(company.id);
    const cancelRequestRef = useRef(false);
    const handleRequestSort = (property) => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
    };
    const createSortHandler = (property) => () => {
        handleRequestSort(property);
    };
    const sortedData = React.useMemo(() => getSortedData(orderBy, order, filteredPhysicians), 
        [order, orderBy, filteredPhysicians]);
    const handleSearch = (value) => {
        dispatch(actions.handleTriggerSummarySearch({ searchTerm: value }));
    };

    const handlePageChange = (event, page) => {
        dispatch(setTriggerSummaryCurrentPage(page));
    };

    const handleNextClick = () => {
        if (currentPage < totalPages) {
            handlePageChange(_, currentPage + 1);
        }
    };

    const handlePreviousClick = () => {
        if (currentPage > 1) {
            handlePageChange(_, currentPage - 1);
        }
    };
    const handlePrescriberProfileModal = (npiId) => {
        if (company.id === REGENERON_COMPANY_ID) {
            dispatch(setSelectedNPI(npiId));
            handleShowModal();
        }

    };
    const handleShowModal = () => {
        setShowModal(!showModal);
    };
    useEffect(() => {
        const handleResize = () => {
            setMaxWidth(calculateMaxWidth());
        };

        window.addEventListener('resize', handleResize);
        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, []);
    const renderNoTriggerData = () => (
        <TableRow>
            <TableCell colSpan={8}>
                <Box p={3} textAlign="center">
                    <Typography className="text-sm-regular gray-900">
                            No trigger engagement data to display
                    </Typography>
                </Box>
            </TableCell>
        </TableRow>
    );

    const renderTriggerData = () => totalPhysicianCount > 0
        ? sortedData.map((row, index) => (
            <>
                <TableRow
                    className={handleRowClassForEngagementsTable(index, company.id)}
                    key={row.npi} onClick={() => handlePrescriberProfileModal(row?.npi)}
                >
                    <TableCell
                        className="table-row-data no-border w-132px"
                        rowSpan={2}
                    >
                        {row?.trigger_names?.join(', ') || ''}
                    </TableCell>
                    <TableCell
                        className="table-row-data no-border min-w-132px"
                        rowSpan={2}
                    >
                        {row.triggerDate}
                    </TableCell>
                    <TableCell
                        className="table-row-data no-table-border-cell gray-900 no-border"
                        rowSpan={2}
                    >
                        {row.npi}
                    </TableCell>
                    <TableCell
                        className="table-row-data no-border"
                        rowSpan={2}
                    >
                        {row.name}
                    </TableCell>
                    {/* Location and District */}
                    <TableCell
                        className="table-row-data no-table-border-cell gray-900 no-border"
                        rowSpan={1}
                        style={{ minWidth: '200px' }}
                    >
                        {row.address +
                                ', ' +
                                row.city +
                                ', ' +
                                row.state +
                                ',' +
                                row?.zip}
                    </TableCell>
                    <TableCell
                        className="table-row-data no-border"
                        rowSpan={2}
                    >
                        <Box className="flex-row-center br-16px padding-tb-4-rl-12 bg-primary-50 primary-700 text-align-center">
                            {
                                Array.isArray(row.segment) ?
                                    row?.segment.join(', ')
                                    : row.segment
                            }
                        </Box>
                    </TableCell>
                    {
                        !handleHideSpecialtyColumns(company.id) ?
                            <TableCell className="table-row-data no-border" rowSpan={2}>
                                {row?.specialty}
                            </TableCell> : null
                    }
                    {
                        showFMT ?
                            <TableCell
                                align='left'
                                className="table-row-data no-border"
                                rowSpan={2}
                            >
                                <Box className="flex-row-start-center gap-12">
                                    <Avatar
                                        alt={handleIcons(row.digital_engagement_priority).icon}
                                        className={`trigger-table-avatar ${handleIcons(row.digital_engagement_priority).color}`}
                                        src={' '}
                                    />
                                    {row.digital_engagement_priority}
                                </Box>
                            </TableCell> :
                            <TableCell
                                className="table-row-data no-border"
                                rowSpan={2}
                            >
                                <Box className="flex-row-start-center gap-12">
                                    <Box
                                        className={`br-20px padding-10px ${row.digitalEngagement === 'Yes'
                                            ? 'bg-success-100'
                                            : 'bg-error-100'
                                        }`}
                                    >
                                        <img
                                            src={
                                                row.digitalEngagement === 'Yes'
                                                    ? CheckCircleIcon
                                                    : CrossCircleIcon
                                            }
                                        />
                                    </Box>
                                    {row.digitalEngagement}
                                </Box>
                            </TableCell>
                    }
                    {showFMT &&
                            <TableCell
                                align='left'
                                className="table-row-data no-border"
                                rowSpan={2}
                            >
                                <Box className="flex-row-start-center gap-12">
                                    <Avatar
                                        alt={handleIcons(row.suggest_fmt_trigger).icon}
                                        className={`trigger-table-avatar ${handleIcons(row.suggest_fmt_trigger).color}`}
                                        src={' '}
                                    />
                                    {row?.suggest_fmt_trigger}
                                </Box>
                            </TableCell>
                    }

                    <TableCell
                        className="table-row-data no-border"
                        rowSpan={2}
                    >
                        <Box className="flex-row-start-center gap-12">
                            <Box
                                className={`br-20px padding-10px ${row.fieldEngagement === 'Yes'
                                    ? 'bg-success-100'
                                    : 'bg-error-100'
                                }`}
                            >
                                <img
                                    src={
                                        row.fieldEngagement === 'Yes'
                                            ? CheckCircleIcon
                                            : CrossCircleIcon
                                    }
                                />
                            </Box>
                            {row.fieldEngagement}
                        </Box>
                    </TableCell>

                </TableRow>
                <TableRow
                    className={
                        index % 2 === 0 ? 'odd-row-background' : ''
                    }
                    key={row.npi + '_district'}
                >
                    <TableCell
                        className="table-row-data padding-0 no-border"
                        rowspan={1}
                    >
                        <span className="font-weight-700">
                                District:{' '}
                        </span>
                        {row.district}
                    </TableCell>
                </TableRow>
            </>
        ))
        : renderNoTriggerData();

    const handleCSVData = async () => {
        const csvName = 'Triggers Summary';
        const isHidden = handleHideSpecialtyColumns(company.id);
        const csvDataArray = [];
        const csvHeaders = [
            { label: 'Trigger Name', key: 'trigger_name' },
            { label: 'Trigger Date', key: 'triggerDate' },
            { label: 'NPI', key: 'npi' },
            { label: 'Physician Name', key: 'name' },
            { label: 'Location', key: 'location' },
            ...(isHidden ? [] : [{ label: 'Physician Specialty', key: 'specialty' }]),
            { label: 'Physician Segment', key: 'segment' },
            ...(showFMT ? [{ label: 'Digital Engagement', key: 'digital_engagement_priority' }] : [{ label: 'Digital Engagement', key: 'digitalEngagement' }]),
            ...(showFMT ? [{ label: 'FMT Match', key: 'suggest_fmt_trigger' }] : []),
            { label: 'Field Engagement', key: 'fieldEngagement' }

        ];
        setIsLoading(true);
        dispatch(setDownloading(true));
        cancelRequestRef.current = false;
        
        const finalData = selectedTriggers.map(item => item.toLowerCase());
        const uniqueNames = [...new Set(finalData)];
        const calculateTotalPages = Math.ceil((totalPhysicianCount) / 1000);
        if (uniqueNames.length === finalData.length) {
            for (let page = 1; page <= calculateTotalPages; page++) {
                if (cancelRequestRef.current) {
                    break;
                }
                //const position = (page * 50) - 50;
                try {
                    const result = await dispatch(getTriggersSummaryPaginate({
                        companyId: company.id,
                        startRange: startDateRange,
                        endRange: endDateRange,
                        batchNo: page,
                        batchSize: 1000,
                        triggerNames: { 'triggers': uniqueNames },
                        type: 'download'
                    }));
                    const data = result.payload?.triggerEngagement?.physicians; // Adjust this line if your API response structure differs
        
                    if (data && data.length) {
                        data.map((physician) => {
                            const datum_export = {
                                serialNumber: physician.serialNumber,
                                triggerDate: formatDateForExport(physician.triggerDate),
                                trigger_name: physician?.trigger_names?.join(', '),
                                npi: physician.npi,
                                name: physician.name,
                                segment: physician.segment,
                                ...(isHidden ? {} : { specialty: physician.specialty }),
                                location: `${physician.address} ${physician.city} ${physician.state} ${physician.zip}  ${'District:' + physician.district}`,
                                ...(showFMT ? { digital_engagement_priority: physician.digital_engagement_priority } : { digitalEngagement: physician.digitalEngagement }),
                                ...(showFMT ? { suggest_fmt_trigger: physician?.suggest_fmt_trigger } : {}),
                                fieldEngagement: physician.fieldEngagement
                            };
                            csvDataArray.push(datum_export);
                        });
                    }
                } catch (error) {
                    if (!cancelRequestRef.current) {
                        
                    }
                }
            }
        }
        
        if (!cancelRequestRef.current && csvDataArray.length > 0) {
            setCSVData({
                name: csvName,
                headers: csvHeaders,
                data: csvDataArray
            });
            setIsDownloading(false);
        }
        
        setIsLoading(false);
        dispatch(setDownloading(false));
        
        return {
            data: csvDataArray,
            headers: csvHeaders,
            filename: csvName.replace(/\.[^/.]+$/, '') + '.csv',
            target: '_blank'
        };
    };
        
    useEffect(() => () => {
        cancelRequestRef.current = true;
    }, []);
    useEffect(() => () => {
        if(selectedTriggers.length){
            setIsLoading(false);
            cancelRequestRef.current = true; 
            setIsDownloading(true);
            actions.setDownloading(false);
            setCSVData([]);
        }
    }, [selectedTriggers]);
    return (
        <Box className="box-sizing-border-box border-1-gray br-8px" sx={{ maxWidth: maxWidth, width: '100%', minWidth: 0 }}>
            {/* Table Header */}
            <Box className="padding-tb-20-rl-24 border-bottom-gray">
                <Box className="flex-row-space-between-center">
                    <Box>
                        <Typography className="text-md-medium gray-900">
                            See Full List Below
                        </Typography>
                    </Box>
                    <Box className="d-flex gap-24">
                        <Box>
                            {/* Search Box */}
                            <Box>
                                <Paper
                                    className='no-box-shadow'
                                    component="form"
                                    sx={{
                                        p: '2px 4px', display: 'flex', alignItems: 'center', width: {
                                            md: 200, lg: 250, xl: 320 
                                        }, border: '1px solid #D0D5DD', borderRadius: '8px' 
                                    }}
                                >
                                    <IconButton aria-label="search" sx={{ p: '10px' }} type="button">
                                        <SearchIcon />
                                    </IconButton>
                                    <InputBase
                                        inputProps={{ 'aria-label': 'search google maps' }}
                                        placeholder="Search"
                                        sx={{ ml: 1, flex: 1 }}
                                        onChange={(e) => handleSearch(e.target.value)}
                                    />
                                </Paper>
                            </Box>
                        </Box>
                        <Box className="padding-6px">
                            {isDownloading ? (
                                <CustomButton
                                    buttonColor="#AD509E"
                                    buttonDisabled ={isLoading}
                                    handleClick={handleCSVData} // Keep the click handler here
                                    icon={<DownloadIcon />}
                                    title={isLoading ? 'Downloading...' : 'Export All Data'}
                                    titleColor="#FFFFFF"
                                />
                            ) : (
                                <CSVLink
                                    data={csvData.data}
                                    filename="Triggers Summary.csv"
                                    headers={csvData.headers}
                                >
                                    <CustomButton
                                        buttonColor="#AD509E"
                                        icon={<DownloadIcon />}
                                        title="Ready For Export"
                                        titleColor="#FFFFFF"
                                    />
                                </CSVLink>
                            )}
                        </Box>

                    </Box>

                </Box>
            </Box>

            {/* Table Content */}
            <Box sx={{ width: '100%', overflowX: 'auto' }}>
                <TableContainer
                    className="no-box-shadow no-border"
                    sx={{ overflowX: 'auto' }}
                    component={Paper}
                >
                    <Table className="table-border-collapse no-border">
                        <TableHead>
                            <TableRow>
                                {TRIGGER_TABLE_HEADERS
                                    ?.filter(title => {
                                        if (title.key === 'specialty' && handleHideSpecialtyColumns(company.id)) {
                                            return false;
                                        }
                                        if (title.key === 'suggest_fmt_trigger' && !showFMT) {
                                            return false;
                                        }
                                        return true;
                                    })
                                    .map(title => (
                                        <TableCell className={title.key === 'location' ? 'table-headers-title-no-side-padding' : 'table-headers-title'} key={title.key}>
                                            <TableSortLabel
                                                active={orderBy === title.key}
                                                direction={orderBy === title.key ? order : 'asc'}
                                                onClick={createSortHandler(title.key)}
                                            >
                                                {title.label}
                                            </TableSortLabel>
                                        </TableCell>
                                    ))}
                            </TableRow>


                        </TableHead>
                        <TableBody>
                            {isTriggerSummaryPaginated
                                ? Array.from({ length: 11 }).map((_, index) => (
                                    <TableRow key={index}>
                                        {Array.from({ length: !handleHideSpecialtyColumns(company.id) ? 10 : 9 }).map(
                                            (_, colIndex) => (
                                                <TableCell key={colIndex}>
                                                    <Skeleton animation="wave" />
                                                </TableCell>
                                            )
                                        )}
                                    </TableRow>
                                ))
                                : renderTriggerData()}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Box>

            {/* Table Footer */}
            {totalPages ? (
                <Box className="padding-tb-16-rl-24 border-top-gray">
                    <Box className="flex-row-space-between-center">
                        <Box>
                            <CustomButton
                                buttonColor="#FFFFFF"
                                buttonDisabled={!(currentPage !== 1)}
                                customStyle={{ borderRadius: '8px' }}
                                handleClick={handlePreviousClick}
                                icon={<ArrowLeftIcon />}
                                title="Previous"
                                titleColor="#344054"
                            />
                        </Box>

                        {/* Pagination */}
                        <Box>
                            <Pagination
                                hideNextButton
                                hidePrevButton
                                count={totalPages}
                                page={currentPage}
                                shape="rounded"
                                onChange={handlePageChange}
                            />
                        </Box>

                        <Box>
                            <CustomButton
                                buttonColor="#FFFFFF"
                                buttonDisabled={!(totalPages > currentPage)}
                                customStyle={{ borderRadius: '8px' }}
                                handleClick={handleNextClick}
                                tailIcon={<ArrowRightIcon />}
                                title="Next"
                                titleColor="#344054"
                            />
                        </Box>
                    </Box>
                </Box>
            ) : null}
            <CustomModal
                component={<PrescribersProfile handleClose={handleShowModal} />}
                modalOpen={showModal}
                onClose={handleShowModal}
            />
        </Box>
    );
}

export default TriggerTable;
